(() => {
  const clamp = (n, min = 0, max = 1) => Math.min(max, Math.max(min, n));
  // One weighted timeline drives the film, chapter copy and navigation in both directions.
  const locate = (progress, weights) => {
    const sum = weights.reduce((a, b) => a + b, 0);
    if (progress >= 1) return { index: weights.length - 1, local: 1 };
    let distance = clamp(progress) * sum;
    for (let index = 0; index < weights.length; index += 1) {
      if (distance < weights[index] || index === weights.length - 1) {
        return { index, local: clamp(distance / weights[index]) };
      }
      distance -= weights[index];
    }
  };
  const smoothstep = (n) => {
    const value = clamp(n);
    return value * value * (3 - 2 * value);
  };
  const overlayOpacity = (slot, local, count) => Math.min(
    slot === 0 ? 1 : smoothstep(local / 0.14),
    slot === count - 1 ? 1 : 1 - smoothstep((local - 0.84) / 0.16)
  );
  // A large wheel movement or chapter jump must still let the old overlay leave first.
  const advanceOverlay = (current, target, elapsed) => {
    const switching = current.index !== target.index;
    const goal = switching ? 0 : target.opacity;
    const step = clamp(elapsed, 0, 32) / (goal < current.opacity ? 200 : 320);
    const opacity = goal < current.opacity
      ? Math.max(goal, current.opacity - step) : Math.min(goal, current.opacity + step);
    if (switching && opacity === 0) {
      return { index: target.index, opacity: 0, pending: target.opacity > 0 };
    }
    return { index: current.index, opacity, pending: switching || opacity !== goal };
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = { locate, overlayOpacity, advanceOverlay };
  if (typeof document === 'undefined') return;

  const instances = new Map();
  class RoutineJourney {
    constructor(root) {
      this.root = root;
      this.stage = root.querySelector('[data-journey-stage]');
      this.scenes = Array.from(root.querySelectorAll('[data-journey-scene]'));
      this.chapters = Array.from(root.querySelectorAll('[data-journey-chapter]'));
      this.buttons = Array.from(root.querySelectorAll('[data-journey-jump]'));
      this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.small = window.matchMedia('(max-width: 749px)');
      this.short = window.matchMedia('(max-height: 639px)');
      const requestedMode = new URLSearchParams(window.location.search).get('dg_journey');
      this.mode = requestedMode === 'film' ? 'film' : requestedMode === 'opening' ? 'opening' : root.dataset.mode;
      root.dataset.mode = this.mode;
      document.documentElement.dataset.dgJourneyMode = this.mode;
      this.indices = this.mode === 'opening' || this.mode === 'film' ? [0, 2, 4] : [0, 1, 2, 3, 4];
      this.weights = this.mode === 'film' ? [0.25, 0.55, 0.2] : this.indices.map((i) => i === 0 ? 1.3 : 1);
      if (this.mode === 'film') {
        const video = this.scenes[0].querySelector('video');
        video.dataset.src = root.dataset.filmSrc;
        video.dataset.srcMobile = root.dataset.filmSrcMobile;
        this.scenes[0].querySelector('img').src = root.dataset.filmPoster;
        this.chapters[0].querySelector('img').src = root.dataset.filmPoster;
      }
      this.total = this.weights.reduce((a, b) => a + b, 0);
      this.raf = 0;
      this.active = -1;
      this.overlay = null;
      this.overlayTime = 0;
      this.destroyed = false;
      this.schedule = this.schedule.bind(this);
      this.update = this.update.bind(this);
      this.onPreference = () => this.configure();
      this.jumpListeners = [];
      this.buttons.forEach((button, index) => {
        const listener = (event) => {
          if (!this.interactive) return;
          event.preventDefault();
          const slot = this.indices.indexOf(index);
          if (slot < 0) return;
          const before = this.weights.slice(0, slot).reduce((a, b) => a + b, 0);
          const fraction = (before + this.weights[slot] * 0.18) / this.total;
          const top = root.getBoundingClientRect().top + window.scrollY - this.headerHeight;
          const travel = root.offsetHeight - this.stage.offsetHeight;
          window.scrollTo({ top: top + travel * fraction, behavior: 'instant' });
        };
        button.addEventListener('click', listener);
        this.jumpListeners.push(listener);
      });
      this.scenes.forEach((scene) => {
        const video = scene.querySelector('video');
        video.addEventListener('loadeddata', () => {
          scene.classList.add('has-film');
          this.schedule();
        });
        video.addEventListener('seeked', this.schedule);
        video.addEventListener('error', () => {
          scene.classList.remove('has-film');
          scene.classList.add('film-unavailable');
          if (this.mode === 'film' || this.scenes.every((item) => item.classList.contains('film-unavailable'))) {
            this.root.classList.remove('is-interactive');
            this.interactive = false;
            this.showStatic();
          }
        });
      });
      window.addEventListener('scroll', this.schedule, { passive: true });
      window.addEventListener('resize', this.schedule);
      this.reduce.addEventListener('change', this.onPreference);
      this.short.addEventListener('change', this.onPreference);
      this.configure();
    }

    configure() {
      this.interactive = !this.reduce.matches && !this.short.matches && !(navigator.connection && navigator.connection.saveData)
        && new URLSearchParams(window.location.search).get('dg_motion') !== 'off';
      this.active = -1;
      this.overlay = null;
      this.overlayTime = 0;
      this.root.classList.toggle('is-interactive', this.interactive);
      this.root.style.setProperty('--journey-travel', `${this.mode === 'film' ? 360 : this.indices.length * 110 + 100}svh`);
      this.root.style.setProperty('--journey-travel-mobile', `${this.mode === 'film' ? 380 : this.indices.length * 100 + 100}svh`);
      if (!this.interactive) this.showStatic();
      this.schedule();
    }

    showStatic() {
      this.chapters.forEach((chapter, index) => {
        const visible = this.indices.includes(index);
        chapter.hidden = !visible;
        chapter.inert = false;
        chapter.removeAttribute('aria-hidden');
        chapter.style.removeProperty('opacity');
      });
      this.scenes.forEach((scene) => scene.querySelector('video').pause());
    }

    hydrate(index) {
      const scene = this.scenes[index];
      const video = scene.querySelector('video');
      if (video.dataset.hydrated || !this.interactive) return;
      video.dataset.hydrated = 'true';
      video.src = this.small.matches && video.dataset.srcMobile ? video.dataset.srcMobile : video.dataset.src;
      video.preload = 'auto';
      video.load();
      video.pause();
    }

    seek(index, local) {
      const video = this.scenes[index].querySelector('video');
      if (video.readyState < 1 || !Number.isFinite(video.duration) || video.seeking) return;
      // Hold the opening/closing frames so the visitor can read without forcing playback.
      const filmProgress = this.mode === 'film' ? clamp(local) : clamp((local - 0.08) / 0.84);
      const target = filmProgress * Math.max(video.duration - 0.06, 0);
      if (Math.abs(video.currentTime - target) > 0.025) {
        try { video.currentTime = target; } catch (error) { /* Retry after media initialization. */ }
      }
    }

    schedule() {
      if (this.raf || this.destroyed) return;
      this.raf = window.requestAnimationFrame(this.update);
    }

    update() {
      this.raf = 0;
      if (this.destroyed) return;
      const rect = this.root.getBoundingClientRect();
      document.documentElement.dataset.dgJourneyInView = String(this.interactive && rect.bottom > 0 && rect.top < window.innerHeight);
      const header = document.querySelector('.section-header');
      this.headerHeight = header ? Math.round(clamp(header.getBoundingClientRect().bottom, 0, window.innerHeight)) : 0;
      this.root.style.setProperty('--journey-header', `${this.headerHeight}px`);
      this.root.style.setProperty('--journey-preview-bar', document.querySelector('#PBarNextFrame') ? '70px' : '0px');
      if (!this.interactive) return;
      const travel = Math.max(this.root.offsetHeight - this.stage.offsetHeight, 1);
      const progress = clamp((this.headerHeight - rect.top) / travel);
      const state = locate(progress, this.weights);
      const index = this.indices[state.index];
      const next = this.indices[Math.min(state.index + 1, this.indices.length - 1)];
      const blend = next === index ? 0 : clamp((state.local - 0.9) / 0.1);
      this.root.dataset.activeChapter = String(index);
      this.root.style.setProperty('--journey-progress', progress.toFixed(4));
      this.scenes.forEach((scene, i) => {
        const alpha = this.mode === 'film' ? (i === 0 ? 1 : 0) : i === index ? 1 : i === next ? blend : 0;
        scene.style.opacity = String(alpha);
        scene.style.zIndex = i === next && next !== index ? '2' : '1';
        scene.style.setProperty('--scene-scale', this.mode === 'film' ? '1' : String(1 + (i === index ? state.local : 0) * 0.02));
      });
      const targetOverlay = { index, opacity: overlayOpacity(state.index, state.local, this.indices.length) };
      const now = performance.now();
      this.overlay = this.overlay
        ? advanceOverlay(this.overlay, targetOverlay, this.overlayTime ? now - this.overlayTime : 16)
        : { ...targetOverlay, pending: false };
      this.overlayTime = this.overlay.pending ? now : 0;
      this.chapters.forEach((chapter, i) => {
        const shown = i === this.overlay.index && this.overlay.opacity > 0;
        const current = shown && i === index && this.overlay.opacity > 0.1;
        chapter.style.opacity = shown ? String(this.overlay.opacity) : '0';
        chapter.hidden = !shown;
        chapter.inert = !current;
        chapter.setAttribute('aria-hidden', String(!current));
      });
      if (this.overlay.pending) this.schedule();
      if (this.active !== index) {
        this.active = index;
        this.buttons.forEach((button, i) => {
          button.hidden = !this.indices.includes(i);
          if (i === index) button.setAttribute('aria-current', 'step');
          else button.removeAttribute('aria-current');
        });
      }
      if (rect.bottom > this.headerHeight && rect.top < window.innerHeight) {
        if (this.mode === 'film') {
          this.hydrate(0);
          this.seek(0, progress);
        } else {
          this.hydrate(index);
          this.seek(index, state.local);
          if (state.local > 0.55 && next !== index) this.hydrate(next);
          if (blend > 0) this.seek(next, 0);
        }
      }
    }

    destroy() {
      this.destroyed = true;
      document.documentElement.dataset.dgJourneyInView = 'false';
      if (this.raf) window.cancelAnimationFrame(this.raf);
      window.removeEventListener('scroll', this.schedule);
      window.removeEventListener('resize', this.schedule);
      this.reduce.removeEventListener('change', this.onPreference);
      this.short.removeEventListener('change', this.onPreference);
      this.buttons.forEach((button, index) => button.removeEventListener('click', this.jumpListeners[index]));
      this.scenes.forEach((scene) => scene.querySelector('video').pause());
      instances.delete(this.root);
    }
  }
  const mount = (scope = document) => {
    scope.querySelectorAll('[data-dg-routine-journey]').forEach((root) => {
      if (!instances.has(root)) instances.set(root, new RoutineJourney(root));
    });
  };
  mount();
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((instance, root) => {
      if (event.target.contains(root)) instance.destroy();
    });
  });
})();
